import { navigateWorkbench } from "../lib/studioNavigation";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { Select } from "../components/rumonai";
import { readDraft, useDraft, writeDraft } from "../lib/drafts";
import { createdWorkPage, panelHref, sameWorkspace, workspaceEntries, workspaceHref, workspaceMemory, workspaceTitle } from "../lib/workspaces";
import { surfaceRequests, type SavedSurfaceRequest, type SurfacePages, type SurfaceReceipt, type SurfaceRequest } from "../lib/surfaceRequests";
import { ErrorState, LoadingState } from "../shell/ui";
import { CommandTrail } from "../pages/CommandTrail";
import { PanelView } from "./PanelView";
import { SurfaceVisibility } from "./SurfaceHost";
import { MainPanelVisibility } from "./MainPanelPresence";
import "./SurfaceWorkspace.css";

export function SurfaceWorkspace({ sessionId, panelKey, createRequest = false, context, navigationVisit = 0, onContextChange, active, version, onToast, onOpenChat }: {
  sessionId: string; panelKey?: string; createRequest?: boolean; context?: Record<string, string>;
  navigationVisit?: number;
  onContextChange?: (panelKey: string, context: Record<string, string>) => void;
  active: boolean; version: number | string;
  onToast: (text: string, tone: "good" | "critical") => void; onOpenChat: () => void;
}) {
  const inheritedVisibility = useContext(SurfaceVisibility);
  const mainPanelVisible = useContext(MainPanelVisibility);
  const [data, setData] = useState<SurfacePages | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [refresh, setRefresh] = useState(0);
  const [selected, select] = useDraft("surface-workspace:selected", "");
  const [expanded, setExpanded] = useState(true);
  const initialSession = useRef(sessionId);
  useEffect(() => {
    if (!active) return;
    let live = true;
    surfaceRequests.pages().then(result => {
      if (live) { setData(result); setError(null); }
    }).catch(failure => { if (live) setError(failure); });
    return () => { live = false; };
  }, [active, version, refresh]);
  const entries = workspaceEntries(data?.pages ?? []);
  const createdPages = entries.filter(createdWorkPage);
  const requested = data?.pages.find(page => page.key === (panelKey || selected));
  const current = entries.find(page => page.key === (panelKey || selected))
    ?? entries.find(page => requested && sameWorkspace(page, requested)) ?? entries[0];
  const memory = current ? workspaceMemory(current) : "";
  const viewContext = useMemo(() => context ?? readDraft<Record<string, string>>(
    `navigation:${memory}`,
    { session: initialSession.current }), [context, memory, current?.key]);
  // A history visit (Back, Forward, a link) that lands on another object than the page last showed opens that
  // object's page afresh; a return to the object already shown keeps the page as it is, edits, focus and scroll.
  const landed = useRef({ visit: navigationVisit, context: JSON.stringify(viewContext), reopened: 0 });
  const shown = JSON.stringify(viewContext);
  if (landed.current.visit !== navigationVisit) {
    if (landed.current.context !== shown) landed.current.reopened += 1;
    landed.current = { ...landed.current, visit: navigationVisit, context: shown };
  }
  useEffect(() => {
    if (!current || createRequest) return;
    writeDraft("navigation:work", panelHref(current.key, viewContext));
  }, [current?.key, createRequest, viewContext]);
  const remember = (next: Record<string, string>) => {
    if (!current) return;
    landed.current.context = JSON.stringify(next);
    if (onContextChange) { onContextChange(current.key, next); return; }
    writeDraft(`navigation:panel:${current.key}`, next);
    writeDraft(`navigation:${memory}`, next);
    writeDraft("navigation:work", panelHref(current.key, next));
  };
  return <div className={`surface-workspace${createRequest ? " surface-workspace-create" : ""}`}>
    <header className="page-head"><div>
      <h1 className={`page-title${!createRequest && entries.length > 1 ? " visually-hidden" : ""}`} tabIndex={-1}>{createRequest ? "添加界面" : current ? workspaceTitle(current) : "工作空间"}</h1>
      {!createRequest && entries.length > 1 ? <label className="workspace-picker">当前界面<Select label="当前界面" value={current?.key ?? ""} onValueChange={value => {
        select(value); setExpanded(true);
        const page = entries.find(page => page.key === value);
        if (panelKey && page) navigateWorkbench(workspaceHref(page, sessionId));
      }} items={entries.map(page => ({ value: page.key, label: workspaceTitle(page) }))} /></label> : null}</div>
      <div className="surface-workspace-actions">
        {!createRequest && current ? <button type="button" className="btn" aria-expanded={expanded} aria-controls="active-work-surface" onClick={() => setExpanded(value => !value)}>{expanded ? "收起界面" : "展开界面"}</button> : null}
        {createRequest ? <a className="btn" href={`#/workspace?${new URLSearchParams({ session: sessionId })}`}>返回工作区</a>
          : data?.authoring.available ? <a className="btn" href={`#/workspace?${new URLSearchParams({ create: "1", session: sessionId })}`}><Icon name="plus" size={18} />添加界面</a> : null}
        {!createRequest ? <button type="button" className="btn btn-icon" aria-label="刷新界面" title="刷新" disabled={!active} onClick={() => setRefresh(value => value + 1)}><Icon name="refresh" size={18} /></button> : null}
      </div>
    </header>
    {createRequest ? <p className="surface-workspace-lead">想用这个页面做什么？让 IP 为你搭建。</p> : null}
    {!active ? <p className="surface-read-notice" role="status">连接恢复后可继续操作。</p> : null}
    {error ? <ErrorState error={error} onRetry={() => setRefresh(value => value + 1)} /> : null}
    {!data && !error ? <LoadingState label="正在读取当前界面" /> : null}
    {data ? <>
      {createdPages.length ? <section className="surface-created-pages">
        <h2>已创建的界面</h2>
        <nav aria-label="已创建的界面">{createdPages.map(page => <a key={page.key} className="btn btn-quiet"
          href={workspaceHref(page, sessionId)} aria-current={page.key === current?.key ? "page" : undefined}>
          <Icon name="content" size={16} /><span>{page.title}</span>
        </a>)}</nav>
      </section> : null}
      {createRequest && data.authoring.available ? <section className="surface-authoring surface-authoring-new">
        <p>页面只在当前 IP 的私人工作区创建。先写用途、要读取的内容和需要的操作；提交后在原对话查看结果，也可要求撤销本次修改。</p>
        <SurfaceRequestForm key={`${sessionId}:new`} sessionId={sessionId} disabled={!active || Boolean(error)} onToast={onToast} onDone={() => setRefresh(value => value + 1)} />
        <SurfaceRequestHistory requests={data.requests ?? []} pages={data.pages} />
      </section> : null}
      {!createRequest && (current ? <>
        <SurfaceVisibility.Provider value={Boolean(inheritedVisibility && active && expanded)}>
          <MainPanelVisibility.Provider value={mainPanelVisible && expanded}>
          <section id="active-work-surface" hidden={!expanded} className="surface-workspace-stage">
            <PanelView key={`${current.key}:${current.generation_id}:${landed.current.reopened}`} panelKey={current.key} context={viewContext} heading="h2" presentation="embedded" onToast={onToast} onContextChange={remember} />
          </section>
          </MainPanelVisibility.Provider>
        </SurfaceVisibility.Provider>
        {!expanded ? <p className="surface-collapsed">界面已收起</p> : null}
      </> : createdPages.length ? null : <section className="surface-workspace-empty"><Icon name="content" size={34} /><h2>还没有界面</h2>
        <p>告诉 Agent 你想做什么，一起开始。</p>
        <button type="button" className="btn btn-accent" onClick={onOpenChat}>和 IP 说说想法</button></section>)}
      {!createRequest || !data.authoring.available ? <details className="surface-authoring" open={createRequest ? true : undefined}>
        <summary>调整这个界面</summary>
        <p>修改只影响这个 IP 的工作面，不会改动其他 IP。内置界面会先保留为可修改副本；不满意时可在原对话要求撤销本次界面修改。</p>
        <button type="button" className="btn" onClick={onOpenChat}>在对话中调整</button>
        {data.authoring.available ? <><SurfaceRequestForm key={`${sessionId}:${current?.workspace?.key || current?.key || "new"}`}
          sessionId={sessionId} panelKey={current?.editable || current?.surface_template ? current.key : undefined}
          disabled={!active || Boolean(error)} onToast={onToast} onDone={() => setRefresh(value => value + 1)} />
          <SurfaceRequestHistory requests={data.requests ?? []} pages={data.pages} /></>
          : <p className="surface-read-notice">暂时无法创建页面，可让 IP 帮忙准备。</p>}
      </details> : null}
    </> : null}
  </div>;
}

/**
 * What you asked for, and what came of it.
 *
 * Read from the console's own record rather than from this tab: the form's
 * draft, its receipt and its progress used to live in `sessionStorage` alone,
 * so a person who reloaded in a new tab — or came back the next morning — was
 * told nothing at all about a page they had asked for (D402).
 */
function SurfaceRequestHistory({ requests, pages }: { requests: SavedSurfaceRequest[]; pages: SurfacePages["pages"] }) {
  if (!requests?.length) return null;
  const said = (request: SavedSurfaceRequest) => {
    if (request.state === "draft") return "还没有交出去";
    switch (request.action_state) {
      case "completed": return "已经做完了";
      case "held": return "有一步在等你决定";
      case "failed": return "这次没有做成";
      case "stopped": return "中途停下了";
      case "unknown": return "结果还不清楚";
      default: return "正在处理";
    }
  };
  return <details className="surface-request-history">
    <summary>历史请求 <span className="micro">{requests.length}</span></summary>
    <ul>
      {requests.map(request => {
        const about = pages.find(page => page.key === request.panel_key);
        return <li key={request.command_id}>
          <p className="surface-request-said">{request.text || "（没有写内容）"}</p>
          <p className="micro">{about ? `改的是「${workspaceTitle(about)}」 · ` : ""}{said(request)}</p>
        </li>;
      })}
    </ul>
  </details>;
}

function SurfaceRequestForm({
  sessionId,
  panelKey,
  onToast,
  onDone,
  disabled = false,
}: {
  sessionId: string;
  panelKey?: string;
  disabled?: boolean;
  onToast: (text: string, tone: "good" | "critical") => void;
  onDone: () => void;
}) {
  const [text, setText, draftError] = useDraft(
    `surface-request:text:${sessionId}:${panelKey ?? "new"}`,
    "",
  );
  // The request just sent, under its name: sent again after an answer that
  // did not come back, it is the same request, answered with its first receipt.
  const [pending, setPending] = useState<SurfaceRequest | null>(null);
  const [receipt, setReceipt] = useState<SurfaceReceipt | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <form
      className="surface-request-form"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!text.trim() || busy || disabled) return;
        const body =
          pending?.text === text.trim()
            ? pending
            : {
                text: text.trim(),
                session_id: sessionId,
                command_id: `surface-request-${crypto.randomUUID()}`,
                ...(panelKey ? { panel_key: panelKey } : {}),
              };
        setPending(body);
        setBusy(true);
        setError(null);
        try {
          const result = await surfaceRequests.submit(body);
          setReceipt(result);
          setPending(null);
          setText("");
          onToast(
            "已交给 IP",
            "good",
          );
        } catch (failure) {
          setError(failure);
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="field">
        <span className="visually-hidden">{panelKey ? "界面修改要求" : "界面要求"}</span>
        <textarea
          className="text-input"
          rows={3}
          maxLength={16000}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={panelKey ? "希望调整哪里？" : "例如：一个选题便笺，能记下灵感、标记进度。"}
          disabled={busy}
        />
      </label>
      <button type="submit" className="btn btn-accent" disabled={busy || disabled || !text.trim()}>
        {busy ? "正在提交…" : panelKey ? "提交修改" : "创建界面"}
      </button>
      {error ? <ErrorState error={error} /> : null}
      {draftError ? <p role="alert">{draftError}</p> : null}
      {receipt ? (
        <CommandTrail
          obligationId={receipt.obligation_id}
          label="界面需求"
          ownerMode
          onDone={onDone}
        />
      ) : null}
    </form>
  );
}
